/**
 * <signup-modal> — quiz → email → confirm sign-up dialog.
 *
 * State lives in localStorage. render() derives the visible panel (and the
 * confirm copy) from that state and nothing else, so open() is safe to call
 * from anywhere — auto-open, #hash links, the theme editor — as often as you
 * like. Nothing in the DOM is ever moved or destroyed.
 *
 * Listeners are bound once in connectedCallback and delegated, so the element
 * survives the theme editor re-rendering the section.
 *
 * Requires signup-client.js (window.SignupClient) to be loaded first.
 */
(() => {
  if (customElements.get('signup-modal')) return;

  const KEY_STATE = 'track-modal-sign-up-shown';
  const KEY_SCENT = 'track-modal-scent-preference';

  const S = { NONE: 0, INITIAL: 1, PROMPTED: 2, REGISTERED: 3, CONFIRMED: 4 };

  // Which panel each state shows while the dialog is open. PROMPTED and
  // REGISTERED never survive a close or a page load (see state.reset), so
  // outside an open session the only reachable steps are quiz and confirm.
  const STEP = {
    [S.NONE]: 'quiz',
    [S.INITIAL]: 'quiz',
    [S.PROMPTED]: 'prompt',
    [S.REGISTERED]: 'prompt',
    [S.CONFIRMED]: 'confirm',
  };

  const CLOSE_MS = 450; // fallback only; the real value is read from .closing's transition-duration

  // Only one live instance per page. Themes sometimes clone the header group
  // for a sticky header; a cloned <signup-modal> would otherwise open a second
  // dialog underneath the first and look like a broken state transition.
  let primary = null;

  /* ── storage ──────────────────────────────────────────────────────────── */

  const memory = new Map(); // fallback when localStorage throws (private mode, blocked)
  const store = {
    get(key, fallback) {
      try {
        const raw = localStorage.getItem(key);
        return raw === null ? fallback : JSON.parse(raw);
      } catch {
        return memory.has(key) ? memory.get(key) : fallback;
      }
    },
    set(key, value) {
      memory.set(key, value);
      try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
    },
    remove(key) {
      memory.delete(key);
      try { localStorage.removeItem(key); } catch { /* ignore */ }
    },
  };

  const state = {
    get: () => Number(store.get(KEY_STATE, S.NONE)),
    set: (value) => store.set(KEY_STATE, value),
    getScent: () => store.get(KEY_SCENT, null),
    setScent: (value) => store.set(KEY_SCENT, value),
    /** Abandoned mid-flow: back to the quiz, pick forgotten. A confirmed signup stays confirmed. */
    reset() {
      const current = state.get();
      if (current === S.PROMPTED || current === S.REGISTERED) {
        store.set(KEY_STATE, S.INITIAL);
        store.remove(KEY_SCENT);
      }
    },
  };

  /* ── element ──────────────────────────────────────────────────────────── */

  class SignupModal extends HTMLElement {
    connectedCallback() {
      if (primary && primary !== this && primary.isConnected) {
        this.hidden = true; // duplicate (cloned header etc.): stay inert
        return;
      }
      primary = this;

      this.dialog = this.querySelector('dialog');
      this.panels = [...this.querySelectorAll('section[data-step]')];
      this.form = this.querySelector('form');
      this.config = JSON.parse(this.querySelector('[data-config]').textContent);
      this.hash = `#${this.dataset.trigger}`;
      this.confirmCopy = this.querySelector('[data-description]').textContent.trim();
      this.outcome = null; // 'new' | 'existing' — transient, only picks the confirm copy

      this.dialog.addEventListener('click', this.onAction);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.form.addEventListener('submit', this.onSubmit);
      document.addEventListener('click', this.onTrigger);
      document.addEventListener('shopify:section:select', this.onEditorSelect);
      document.addEventListener('shopify:section:deselect', this.onEditorDeselect);

      this.boot();
    }

    disconnectedCallback() {
      if (primary === this) primary = null;
      document.removeEventListener('click', this.onTrigger);
      document.removeEventListener('shopify:section:select', this.onEditorSelect);
      document.removeEventListener('shopify:section:deselect', this.onEditorDeselect);
    }

    /* ── lifecycle ──────────────────────────────────────────────────────── */

    boot() {
      // Shopify's bot-protection page: the form hasn't landed yet, stay quiet.
      if (location.pathname === '/challenge') return;

      const current = state.get();

      // Back from a native submit (the /challenge round-trip).
      if (current === S.REGISTERED) {
        const outcome = SignupClient.outcomeFromUrl(location.href);
        if (outcome) {
          this.outcome = outcome;
          state.set(S.CONFIRMED);
          this.open();
          return;
        }
      }

      // A new page load mid-flow counts as abandoning it.
      state.reset();

      // Deep link: /any/page#modal
      if (location.hash === this.hash) {
        this.open();
        return;
      }

      if (current === S.NONE && this.dataset.autoOpen === 'true') {
        state.set(S.INITIAL);
        setTimeout(() => this.open(), Number(this.dataset.delay) || 0);
      }
    }

    /** Pure: reads state, sets visibility + copy. Safe to run any number of times. */
    render() {
      const step = STEP[state.get()] ?? 'quiz';
      let active = null;

      for (const panel of this.panels) {
        const on = panel.dataset.step === step;
        panel.hidden = !on;
        if (on) active = panel;
      }

      // Note: data-active-step, not data-step — the dialog must never match the panel selector.
      this.dialog.dataset.activeStep = step;
      this.dialog.classList.toggle('initial', step === 'quiz'); // keeps existing .modal-sign-up.initial styles
      this.dialog.setAttribute('aria-labelledby', active.querySelector('[data-title]').id);

      if (step === 'confirm') {
        const existing = this.outcome === 'existing' && this.config.alreadySignedUp;
        active.querySelector('[data-description]').textContent = existing || this.confirmCopy;
      }

      return active;
    }

    open() {
      const panel = this.render();
      if (!this.dialog.open) this.dialog.showModal();
      panel.focus({ preventScroll: true });
    }

    close() {
      if (!this.dialog.open) return;

      // Closing mid-flow is a clean reset: next open starts at the quiz.
      state.reset();
      this.form.reset();
      this.setError(null);

      this.dialog.classList.add('closing');
      // Read the close duration from CSS so the two can't drift apart.
      const ms = parseFloat(getComputedStyle(this.dialog).transitionDuration) * 1000 || CLOSE_MS;
      setTimeout(() => {
        this.dialog.close();
        this.dialog.classList.remove('closing');
      }, ms);
      if (location.hash === this.hash) {
        history.replaceState(null, '', location.pathname + location.search);
      }
    }

    setBusy(on) {
      this.form.querySelector('[type="submit"]').disabled = on;
      this.form.setAttribute('aria-busy', String(on));
    }

    setError(message) {
      const el = this.querySelector('[data-error]');
      el.hidden = !message;
      el.textContent = message || '';
    }

    /* ── events ─────────────────────────────────────────────────────────── */

    /** Any <a href="…#modal"> anywhere in the document opens the modal. */
    onTrigger = (event) => {
      const link = event.target.closest(`a[href$="${this.hash}"]`);
      if (!link) return;
      event.preventDefault();
      this.open();
    };

    onAction = (event) => {
      const el = event.target.closest('[data-action]');
      if (!el) return;
      switch (el.dataset.action) {
        case 'pick':
          state.setScent(el.dataset.value);
          state.set(S.PROMPTED);
          this.open(); // re-render → prompt panel, focus moves with it
          break;
        case 'close':
          this.close();
          break;
      }
    };

    /** Esc: run the same animated close instead of the browser's instant one. */
    onCancel = (event) => {
      event.preventDefault();
      this.close();
    };

    onSubmit = async (event) => {
      event.preventDefault();
      const email = this.form.elements['contact[email]'].value.trim();
      const { companyId, listId } = this.config.klaviyo || {};

      this.setBusy(true);
      this.setError(null);

      // Klaviyo: best-effort and independent of Shopify's answer. List first,
      // then the scent as a property — two requests by Klaviyo's rules.
      if (companyId && listId) {
        SignupClient.subscribeToList({ companyId, listId, email, source: this.config.source })
          .then(() => SignupClient.setProfileProperties({
            companyId, email, properties: { scent_preference: state.getScent() },
          }))
          .catch((error) => console.warn('[signup-modal] klaviyo', error));
      }

      try {
        const outcome = await SignupClient.postCustomerForm(this.form);

        // Bot protection: hand the round-trip to the browser. boot() picks it
        // up on the way back via the query params Shopify adds.
        if (outcome === 'challenge') {
          state.set(S.REGISTERED);
          this.form.submit();
          return;
        }

        this.outcome = outcome;
        state.set(S.CONFIRMED);
        this.form.reset();
        this.open();
      } catch (error) {
        console.error('[signup-modal]', error);
        this.setError(this.config.errorText);
      } finally {
        this.setBusy(false);
      }
    };

    // Theme editor: clicking the section in the sidebar previews the modal.
    onEditorSelect = (event) => { if (event.target.contains(this)) this.open(); };
    onEditorDeselect = (event) => { if (event.target.contains(this)) this.close(); };
  }

  customElements.define('signup-modal', SignupModal);
})();
